/*
 * Copyright (c)  2020 CalAmp Corp.  All Rights Reserved
 */
import axios, {AxiosInstance} from 'axios';
import {
  AxiosConfig,
  SDConnectConfig,
  SDConnectHeaders,
  ServiceError
} from '../types/services';
import {createAsyncClient} from './AsyncClientManager';
import {setClientAuthorizationHeader} from './ConnectUtils';
import {errorInterceptor} from './ServiceErrors';

axios.defaults.withCredentials = true;

/**
 * Defines a reference to the configured SDCTC Axios HTTP client instance.
 */
export let client: AxiosInstance;

/**
 * Defines a reference to the SDCTC client configuration which is used by all
 * requests.
 */
export let config: AxiosConfig;

// [ION-BMW-PORTAL TODO]: Remove the following lines once the SD subscription
// API endpoint is integrated with the SDConnect API endpoint.
export let subscriptionClient;
export let subscriptionConfig;

/**
 * Allows for providing a global error handler for all SDCTC Connect client requests.
 */
export const setSDConnectErrorHandler = (onError: (ServiceError) => void) => {
  errorInterceptor(client, onError);
};

/**
 * Returns the SDConnect Authorization Token retrieved during authentication.
 *
 * ```typescript
 *    const token = getToken(); // 'Bearer eyJhbGciOiJIUzI1NiIsI ...'
 * ```
 */
export const getToken = (): string => {
  return (config.headers as SDConnectHeaders).Authorization;
};

/**
 * Sets the SDConnect Authorization Token, this function is to be invoked upon
 * successful authentication, passing the service response token as an argument.
 *
 * ```typescript
 *   export const authenticate = async(username: string, password: string): Promise<User> => {
 *     const result = await client({
 *       ...
 *     });
 *     setToken(parse(results, 'authToken') as unknown as string);
 *     return parse(result, 'user');
 *   };
 * ```
 */
export const setToken = (token: string) => {
  const headers = config.headers as SDConnectHeaders;
  headers.Authorization = token;
  client.defaults.headers = headers;
};

/**
 * The setSDConnectConfig function allows for initializing the configuration of the
 * SDCTC Connect Client. This function must be invoked with the given configuration
 * prior to invoking SDConnect client APIs.
 *
 * ```typescript
 *
 *    import {loadConfig} from '@ion/core/src/environment/environment';
 *    import {setSDConnectConfig} from '@ion/core/src/services/SDConnect';
 *
 *    loadConfig((config: SDConnectConfig) => {
 *      setSDConnectConfig(config);
 *        ...
 *      }
 *      ...
 *    );
 *
 * ```
 */
export const setSDConnectConfig = (configuration: SDConnectConfig) => {
  config = {
    ...configuration,
    baseURL         : configuration.serviceURL,
    timeout         : 60000,
    withCredentials : true,
    headers         : {
      'x-app-key'    : configuration.appKey,
      'Content-Type' : 'application/json'
    } as SDConnectHeaders
  };
  client = createAsyncClient(config);
  return client;
};

/**
 * The util function to set Authorization Header in axios client
 *
 * @param token The token to set in header.
 */
export const setAuthorizationHeader = (token: string) => setClientAuthorizationHeader(client, token);
